/**
 * Carries a typed email between the sign-in and sign-up forms via an `email`
 * query param, so a user who lands on the wrong flow (e.g. tries to sign in,
 * learns they have no account, then clicks "Create an account") doesn't have to
 * retype it.
 */

/**
 * Reads the `email` carried on the query string. Kept intentionally loose —
 * Clerk does the authoritative validation on submit; this only guards against
 * pre-filling the field with obvious junk (non-strings, empty, over-long, or
 * missing an `@`).
 */
export function readEmailQuery(value: unknown): string | null {
  if (typeof value !== "string") {
    return null
  }
  const trimmed = value.trim()
  // 254 is the RFC 5321 maximum length of an email address.
  if (trimmed.length === 0 || trimmed.length > 254) {
    return null
  }
  return /^[^\s@]+@[^\s@]+$/.test(trimmed) ? trimmed : null
}

/**
 * Appends an `email` query param to an href, preserving any existing params
 * (e.g. a Clerk invitation ticket). Returns the href unchanged when the email
 * is empty.
 */
export function appendEmailParam(href: string, email: string): string {
  const trimmed = email.trim()
  if (!trimmed) {
    return href
  }
  const [path, existingQuery = ""] = href.split("?")
  const params = new URLSearchParams(existingQuery)
  params.set("email", trimmed)
  return `${path}?${params.toString()}`
}
